import type { GetTokenSilentlyOptions } from '@auth0/auth0-react';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';

type GetAccessTokenSilentlyType = (
  options?: GetTokenSilentlyOptions
) => Promise<string>;
type GetAccessTokenWithPopupType = (
  options?: any,
  config?: any
) => Promise<string | undefined>;

async function getAuthToken(
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn('Silent token acquisition failed:', error);
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error('Failed to acquire access token via popup');
      }
      return token;
    }
    throw error;
  }
}

/**
 * Generate a base64-encoded block ID that is the same length for all blocks.
 * Azure Block Blobs require all block IDs to be the same length.
 */
export function generateBlockId(index: number): string {
  const buffer = new ArrayBuffer(4);
  new DataView(buffer).setUint32(0, index);
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Upload a single audio chunk to the server.
 */
export async function uploadAudioChunk(
  sessionId: string,
  recordingId: string,
  blockId: string,
  chunk: Blob,
  fileName: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ success: boolean; blockId?: string; error?: string }> {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const formData = new FormData();
    formData.append('recordingId', recordingId);
    formData.append('blockId', blockId);
    formData.append('chunk', chunk);
    formData.append('fileName', fileName);

    const response = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/upload-document-chunk`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId
        },
        body: formData
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Chunk upload failed:', errorText);
      return { success: false, error: `HTTP ${response.status}: ${errorText}` };
    }

    const data = await response.json();
    return { success: true, blockId: data.blockId };
  } catch (error) {
    console.error('Error uploading chunk:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Finalize a chunked upload by committing all blocks.
 */
export async function finalizeAudioChunks(
  sessionId: string,
  recordingId: string,
  blockIds: string[],
  fileName: string,
  duration: string | undefined,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType,
  skipAnalysis: boolean = false
): Promise<{ success: boolean; document?: any; error?: string }> {
  try {
    const token = await getAuthToken(
      getAccessTokenSilently,
      getAccessTokenWithPopup
    );

    const response = await fetch(
      `${API_BASE_URL}/transcriptionsessions/${sessionId}/finalize-document-chunks`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-User-Id': userId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recordingId,
          blockIds,
          fileName,
          duration,
          skipAnalysis
        })
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Finalize chunks failed:', errorText);
      return { success: false, error: `HTTP ${response.status}: ${errorText}` };
    }

    const data = await response.json();
    return { success: true, document: data.document };
  } catch (error) {
    console.error('Error finalizing chunks:', error);
    return { success: false, error: String(error) };
  }
}

/**
 * Cancel a chunked upload.
 */
export async function cancelAudioChunks(
  sessionId: string,
  recordingId: string,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<{ success: boolean; error?: string }> {
  // Endpoint does not exist in backend, so we just return success to avoid 404s
  return { success: true };
}
